import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import type { Quote_ } from 'src/api/db/apiQuote'
import { useQuoteApi } from 'src/api/db/apiQuote'
import {
  FormCheckbox,
  FormInput,
  FormTextarea,
  FormWrapper,
} from 'src/shared/components/FormComponents'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'src/shared/components/ui/dialog'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useToast } from 'src/shared/hooks/use-toast'
import { useRequestContext } from '../../../hooks/RequestContext'
import type { InvoiceData, InvoiceParty } from '../types'
import {
  addDaysDateOnly,
  buildDefaultInvoiceData,
  getBillingDocumentData,
  toDateInputValue,
} from '../utils'

const partySchema = z.object({
  name: z.string(),
  addressLine1: z.string(),
  addressLine2: z.string(),
  postal: z.string(),
  city: z.string(),
  country: z.string(),
  orgNumber: z.string(),
  vatNumber: z.string(),
  email: z.string(),
})

const formSchema = z.object({
  invoiceNumber: z.string().min(1, 'Required'),
  issueDate: z.string().min(1, 'Required'),
  supplyDate: z.string(),
  dueDate: z.string().min(1, 'Required'),
  paymentTermsDays: z.coerce.number().min(0).max(365),
  paymentReference: z.string(),
  bankgiro: z.string(),
  plusgiro: z.string(),
  iban: z.string(),
  bic: z.string(),
  currency: z.string().min(1),
  vatRate: z.coerce.number().min(0).max(100),
  fTaxApproved: z.boolean(),
  reverseCharge: z.boolean(),
  ourReference: z.string(),
  yourReference: z.string(),
  notes: z.string(),
  seller: partySchema,
  buyer: partySchema,
})

type FormValues = z.infer<typeof formSchema>

const invoiceToFormValues = (invoice: InvoiceData): FormValues => ({
  invoiceNumber: invoice.invoiceNumber,
  issueDate: toDateInputValue(invoice.issueDate),
  supplyDate: toDateInputValue(invoice.supplyDate),
  dueDate: toDateInputValue(invoice.dueDate),
  paymentTermsDays: invoice.paymentTermsDays,
  paymentReference: invoice.paymentReference,
  bankgiro: invoice.bankgiro,
  plusgiro: invoice.plusgiro,
  iban: invoice.iban,
  bic: invoice.bic,
  currency: invoice.currency,
  vatRate: invoice.vatRate,
  fTaxApproved: invoice.fTaxApproved,
  reverseCharge: invoice.reverseCharge,
  ourReference: invoice.ourReference,
  yourReference: invoice.yourReference,
  notes: invoice.notes,
  seller: { ...invoice.seller },
  buyer: { ...invoice.buyer },
})

const formValuesToInvoiceData = (
  values: FormValues,
  previous: InvoiceData
): InvoiceData => {
  const now = new Date().toISOString()
  return {
    ...previous,
    ...values,
    issueDate: values.issueDate,
    supplyDate: values.supplyDate || values.issueDate,
    dueDate: values.dueDate,
    seller: values.seller as InvoiceParty,
    buyer: values.buyer as InvoiceParty,
    status: previous.status ?? 'draft',
    createdAt: previous.createdAt ?? now,
    updatedAt: now,
  }
}

const PartyFields = ({
  prefix,
  title,
  methods,
}: {
  prefix: 'seller' | 'buyer'
  title: string
  methods: ReturnType<typeof useForm<FormValues>>
}) => {
  const { t } = useLocalization()

  return (
    <Column className="gap-3">
      <h4>{title}</h4>
      <FormInput
        label={t('Name', 'Namn')}
        name={`${prefix}.name`}
        methods={methods}
        className="w-full"
      />
      <FormInput
        label={t('Address', 'Adress')}
        name={`${prefix}.addressLine1`}
        methods={methods}
        className="w-full"
      />
      <FormInput
        label={t('Address line 2', 'Adressrad 2')}
        name={`${prefix}.addressLine2`}
        methods={methods}
        className="w-full"
      />
      <Row className="gap-3">
        <FormInput
          label={t('Postal code', 'Postnummer')}
          name={`${prefix}.postal`}
          methods={methods}
          className="w-32"
        />
        <FormInput
          label={t('City', 'Ort')}
          name={`${prefix}.city`}
          methods={methods}
          className="flex-1"
        />
      </Row>
      <FormInput
        label={t('Country', 'Land')}
        name={`${prefix}.country`}
        methods={methods}
        className="w-full"
      />
      <Row className="gap-3">
        <FormInput
          label={t('Organization number', 'Organisationsnummer')}
          name={`${prefix}.orgNumber`}
          methods={methods}
          className="flex-1"
        />
        <FormInput
          label={t('VAT number', 'Momsregistreringsnummer')}
          name={`${prefix}.vatNumber`}
          methods={methods}
          className="flex-1"
        />
      </Row>
      <FormInput
        label={t('Email', 'E-post')}
        name={`${prefix}.email`}
        methods={methods}
        type="email"
        className="w-full"
      />
    </Column>
  )
}

export const EditInvoiceDialog = ({
  quote,
  open,
  onOpenChange,
}: {
  quote: Quote_
  open: boolean
  onOpenChange: (open: boolean) => void
}) => {
  const { t } = useLocalization()
  const { toast } = useToast()
  const { refetchQuote } = useRequestContext()
  const { updateQuote } = useQuoteApi({ skipListQuery: true })

  const methods = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: invoiceToFormValues(buildDefaultInvoiceData(quote)),
  })

  const skipDueDateSync = useRef(true)

  useEffect(() => {
    if (!open) return
    skipDueDateSync.current = true
    methods.reset(invoiceToFormValues(getBillingDocumentData(quote)))
  }, [open, quote, methods])

  const paymentTermsDays = methods.watch('paymentTermsDays')
  const issueDate = methods.watch('issueDate')

  useEffect(() => {
    if (!open || !issueDate) return
    if (skipDueDateSync.current) {
      skipDueDateSync.current = false
      return
    }
    const days =
      typeof paymentTermsDays === 'number' && Number.isFinite(paymentTermsDays)
        ? paymentTermsDays
        : 30
    methods.setValue('dueDate', addDaysDateOnly(issueDate, days))
  }, [open, issueDate, paymentTermsDays, methods])

  const handleSubmit = async (values: FormValues) => {
    const previous = getBillingDocumentData(quote)
    const invoiceData = formValuesToInvoiceData(values, previous)

    try {
      const result = await updateQuote.mutateAsync({
        id: quote.id,
        payment_terms: values.paymentTermsDays,
        payment_reference: values.paymentReference.trim(),
        invoice_data: invoiceData,
      })
      if (result.error) throw result.error

      refetchQuote()
      toast({
        title: t('Invoice saved', 'Faktura sparad'),
        description: invoiceData.invoiceNumber,
      })
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to save invoice', err)
      toast({
        variant: 'destructive',
        title: t('Could not save invoice', 'Kunde inte spara faktura'),
        description: err instanceof Error ? err.message : undefined,
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('Edit invoice', 'Redigera faktura')}</DialogTitle>
        </DialogHeader>

        <FormWrapper methods={methods} onSubmit={handleSubmit}>
          <Column className="gap-6">
            <Column className="gap-3">
              <h4>{t('Document', 'Dokument')}</h4>
              <Row className="gap-3 flex-wrap">
                <FormInput
                  label={t('Invoice number', 'Fakturanummer')}
                  name="invoiceNumber"
                  methods={methods}
                  className="w-48"
                />
                <FormInput
                  label={t('Issue date', 'Fakturadatum')}
                  name="issueDate"
                  methods={methods}
                  type="date"
                  className="w-44"
                />
                <FormInput
                  label={t('Supply date', 'Leveransdatum')}
                  name="supplyDate"
                  methods={methods}
                  type="date"
                  className="w-44"
                />
              </Row>
              <Row className="gap-3 flex-wrap">
                <FormInput
                  label={t('Payment terms (days)', 'Betalningsvillkor (dagar)')}
                  name="paymentTermsDays"
                  methods={methods}
                  type="number"
                  className="w-44"
                />
                <FormInput
                  label={t('Due date', 'Förfallodatum')}
                  name="dueDate"
                  methods={methods}
                  type="date"
                  className="w-44"
                />
                <FormInput
                  label={t('Currency', 'Valuta')}
                  name="currency"
                  methods={methods}
                  className="w-28"
                />
                <FormInput
                  label={t('VAT rate %', 'Momssats %')}
                  name="vatRate"
                  methods={methods}
                  type="number"
                  className="w-28"
                />
              </Row>
              <Row className="gap-3 flex-wrap">
                <FormInput
                  label={t('Our reference', 'Vår referens')}
                  name="ourReference"
                  methods={methods}
                  className="flex-1 min-w-[10rem]"
                />
                <FormInput
                  label={t('Your reference', 'Er referens')}
                  name="yourReference"
                  methods={methods}
                  className="flex-1 min-w-[10rem]"
                />
              </Row>
              <Row className="gap-4 flex-wrap">
                <FormCheckbox
                  label={t('Approved for F-tax', 'Godkänd för F-skatt')}
                  name="fTaxApproved"
                  methods={methods}
                />
                <FormCheckbox
                  label={t('Reverse charge', 'Omvänd betalningsskyldighet')}
                  name="reverseCharge"
                  methods={methods}
                />
              </Row>
            </Column>

            <PartyFields
              prefix="seller"
              title={t('Seller', 'Säljare')}
              methods={methods}
            />

            <PartyFields
              prefix="buyer"
              title={t('Buyer', 'Köpare')}
              methods={methods}
            />

            <Column className="gap-3">
              <h4>{t('Payment', 'Betalning')}</h4>
              <FormInput
                label={t('Payment reference (OCR)', 'Betalreferens (OCR)')}
                name="paymentReference"
                methods={methods}
                className="w-full"
              />
              <Row className="gap-3 flex-wrap">
                <FormInput
                  label={t('Bankgiro', 'Bankgiro')}
                  name="bankgiro"
                  methods={methods}
                  className="flex-1 min-w-[8rem]"
                />
                <FormInput
                  label={t('Plusgiro', 'Plusgiro')}
                  name="plusgiro"
                  methods={methods}
                  className="flex-1 min-w-[8rem]"
                />
              </Row>
              <Row className="gap-3 flex-wrap">
                <FormInput
                  label="IBAN"
                  name="iban"
                  methods={methods}
                  className="flex-1 min-w-[12rem]"
                />
                <FormInput
                  label="BIC"
                  name="bic"
                  methods={methods}
                  className="w-36"
                />
              </Row>
              <FormTextarea
                label={t('Notes', 'Anteckningar')}
                name="notes"
                methods={methods}
                className="w-full"
              />
            </Column>

            <Row className="justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => onOpenChange(false)}
              >
                {t('Cancel', 'Avbryt')}
              </Button>
              <Button type="submit" disabled={updateQuote.isPending}>
                {updateQuote.isPending
                  ? t('Saving...', 'Sparar...')
                  : t('Save invoice', 'Spara faktura')}
              </Button>
            </Row>
          </Column>
        </FormWrapper>
      </DialogContent>
    </Dialog>
  )
}
